/**
 * ============================================================
 *  Tina Jewellery — IP-Aware Currency Switcher  v4
 *
 *  Flow (first visit):
 *    1. Detect country via IP API
 *    2. Egyptian / unknown → silent, no popup
 *    3. International → show popup with detected country highlighted
 *    4. On pick → POST to /localization (market switch) + apply FX
 *
 *  Flow (returning visitor):
 *    → Read localStorage, apply FX directly, no popup
 *
 *  Flow (footer selector):
 *    → Intercept click, save choice, apply FX, let native form
 *      submit handle the market switch reload
 * ============================================================
 */
(function () {
  'use strict';

  // ── STATIC CONFIG ─────────────────────────────────────────
  const BASE_CURRENCY = 'EGP';

  // Fallback map — augmented at runtime from DOM (data-currency attr)
  const COUNTRY_CURRENCY_MAP = {
    // Egypt (base)
    EG: 'EGP',
    // GCC + Levant
    AE: 'AED', SA: 'SAR', KW: 'KWD', QA: 'QAR', BH: 'BHD', OM: 'OMR',
    JO: 'JOD', LB: 'LBP',
    // Core Europe
    GB: 'GBP', CH: 'CHF',
    DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR',
    AT: 'EUR', PT: 'EUR', GR: 'EUR', IE: 'EUR', FI: 'EUR', SK: 'EUR',
    SI: 'EUR', LT: 'EUR', LV: 'EUR', EE: 'EUR', CY: 'EUR', MT: 'EUR', LU: 'EUR',
    SE: 'SEK', NO: 'NOK', DK: 'DKK',
    PL: 'PLN', RO: 'RON', CZ: 'CZK', HU: 'HUF',
    // North America
    US: 'USD', CA: 'CAD',
    // North Africa + Other
    MA: 'MAD', TN: 'TND', DZ: 'DZD', ZA: 'ZAR', TR: 'TRY',
  };

  const CURRENCY_FORMAT = {
    EGP: { symbol: 'LE ',  dec: 2 },
    // GCC + Levant
    AED: { symbol: 'AED ', dec: 2 },
    SAR: { symbol: 'SAR ', dec: 2 },
    KWD: { symbol: 'KD ',  dec: 3 },
    QAR: { symbol: 'QR ',  dec: 2 },
    BHD: { symbol: 'BD ',  dec: 3 },
    OMR: { symbol: 'OMR ', dec: 3 },
    JOD: { symbol: 'JD ',  dec: 3 },
    LBP: { symbol: 'ل.ل ', dec: 0 },
    // Core Europe
    GBP: { symbol: '£',    dec: 2 },
    EUR: { symbol: '€',    dec: 2 },
    CHF: { symbol: 'CHF ', dec: 2 },
    SEK: { symbol: 'kr ',  dec: 2 },
    NOK: { symbol: 'kr ',  dec: 2 },
    DKK: { symbol: 'kr ',  dec: 2 },
    PLN: { symbol: 'zł ',  dec: 2 },
    RON: { symbol: 'lei ', dec: 2 },
    CZK: { symbol: 'Kč ',  dec: 2 },
    HUF: { symbol: 'Ft ',  dec: 0 },
    // North America
    USD: { symbol: '$',    dec: 2 },
    CAD: { symbol: 'CA$',  dec: 2 },
    // North Africa + Other
    MAD: { symbol: 'MAD ', dec: 2 },
    TND: { symbol: 'DT ',  dec: 3 },
    DZD: { symbol: 'DA ',  dec: 2 },
    ZAR: { symbol: 'R ',   dec: 2 },
    TRY: { symbol: '₺',    dec: 2 },
  };

  const POPUP_META = {
    EG: { flag: '🇪🇬', name: 'Egypt',          sub: 'EGP — Egyptian Pound' },
    // GCC + Levant
    AE: { flag: '🇦🇪', name: 'UAE',             sub: 'AED — Dirham' },
    SA: { flag: '🇸🇦', name: 'Saudi Arabia',    sub: 'SAR — Riyal' },
    KW: { flag: '🇰🇼', name: 'Kuwait',          sub: 'KWD — Dinar' },
    QA: { flag: '🇶🇦', name: 'Qatar',           sub: 'QAR — Riyal' },
    BH: { flag: '🇧🇭', name: 'Bahrain',         sub: 'BHD — Dinar' },
    OM: { flag: '🇴🇲', name: 'Oman',            sub: 'OMR — Rial' },
    JO: { flag: '🇯🇴', name: 'Jordan',          sub: 'JOD — Dinar' },
    LB: { flag: '🇱🇧', name: 'Lebanon',         sub: 'LBP — Pound' },
    // Core Europe
    GB: { flag: '🇬🇧', name: 'United Kingdom',  sub: 'GBP — Pound Sterling' },
    CH: { flag: '🇨🇭', name: 'Switzerland',     sub: 'CHF — Franc' },
    DE: { flag: '🇩🇪', name: 'Germany',         sub: 'EUR — Euro' },
    FR: { flag: '🇫🇷', name: 'France',          sub: 'EUR — Euro' },
    IT: { flag: '🇮🇹', name: 'Italy',           sub: 'EUR — Euro' },
    ES: { flag: '🇪🇸', name: 'Spain',           sub: 'EUR — Euro' },
    NL: { flag: '🇳🇱', name: 'Netherlands',     sub: 'EUR — Euro' },
    BE: { flag: '🇧🇪', name: 'Belgium',         sub: 'EUR — Euro' },
    AT: { flag: '🇦🇹', name: 'Austria',         sub: 'EUR — Euro' },
    PT: { flag: '🇵🇹', name: 'Portugal',        sub: 'EUR — Euro' },
    GR: { flag: '🇬🇷', name: 'Greece',          sub: 'EUR — Euro' },
    IE: { flag: '🇮🇪', name: 'Ireland',         sub: 'EUR — Euro' },
    SE: { flag: '🇸🇪', name: 'Sweden',          sub: 'SEK — Krona' },
    NO: { flag: '🇳🇴', name: 'Norway',          sub: 'NOK — Krone' },
    DK: { flag: '🇩🇰', name: 'Denmark',         sub: 'DKK — Krone' },
    PL: { flag: '🇵🇱', name: 'Poland',          sub: 'PLN — Złoty' },
    RO: { flag: '🇷🇴', name: 'Romania',         sub: 'RON — Leu' },
    CZ: { flag: '🇨🇿', name: 'Czech Republic',  sub: 'CZK — Koruna' },
    HU: { flag: '🇭🇺', name: 'Hungary',         sub: 'HUF — Forint' },
    // North America
    US: { flag: '🇺🇸', name: 'United States',   sub: 'USD — US Dollar' },
    CA: { flag: '🇨🇦', name: 'Canada',          sub: 'CAD — Dollar' },
    // North Africa + Other
    MA: { flag: '🇲🇦', name: 'Morocco',         sub: 'MAD — Dirham' },
    TN: { flag: '🇹🇳', name: 'Tunisia',         sub: 'TND — Dinar' },
    DZ: { flag: '🇩🇿', name: 'Algeria',         sub: 'DZD — Dinar' },
    ZA: { flag: '🇿🇦', name: 'South Africa',    sub: 'ZAR — Rand' },
    TR: { flag: '🇹🇷', name: 'Turkey',          sub: 'TRY — Lira' },
  };

  // ── STORAGE ───────────────────────────────────────────────
  const KEY_COUNTRY = 'tina_chosen_country';
  const KEY_DECIDED = 'tina_country_decided';

  function lsGet(k)    { try { return localStorage.getItem(k); }    catch(_) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); }        catch(_) {} }

  function hasDecided()       { return !!lsGet(KEY_DECIDED); }
  function getStoredCountry() { return lsGet(KEY_COUNTRY); }
  function saveCountry(code)  { lsSet(KEY_DECIDED, '1'); lsSet(KEY_COUNTRY, code); }

  // ── FALLBACK RATES (approximate, used only if all APIs fail) ─
  // 1 EGP → X foreign currency  (based on ~49 EGP/USD mid-2025)
  const FALLBACK_RATES = {
    // GCC + Levant
    AED: 0.0750, SAR: 0.0765, KWD: 0.00626, QAR: 0.0743,
    BHD: 0.00769, OMR: 0.00785, JOD: 0.01446, LBP: 1.836,
    // Core Europe
    GBP: 0.0161, EUR: 0.0188, CHF: 0.0183,
    SEK: 0.2130, NOK: 0.2220, DKK: 0.1410,
    PLN: 0.0808, RON: 0.0939, CZK: 0.4670, HUF: 7.480,
    // North America
    USD: 0.0204, CAD: 0.0278,
    // North Africa + Other
    MAD: 0.2030, TND: 0.0630, DZD: 2.760, ZAR: 0.3810, TRY: 0.6720,
  };

  // ── STATE ─────────────────────────────────────────────────
  let activeCurrency = BASE_CURRENCY;
  let activeRate     = 1;
  let rateCache      = {};
  let ratesPromise   = null;   // deduplicate concurrent fetches
  let originalPrices = new Map();

  // ── LOGGING ───────────────────────────────────────────────
  const LOG = (...args) => console.log('%c[TinaCurrency]', 'color:#c47f17;font-weight:bold', ...args);
  const WARN = (...args) => console.warn('%c[TinaCurrency]', 'color:#c47f17;font-weight:bold', ...args);
  const ERR  = (...args) => console.error('%c[TinaCurrency]', 'color:#c47f17;font-weight:bold', ...args);

  // ── DOM MAP ───────────────────────────────────────────────
  // NOTE: we do NOT read data-currency from the footer links to update COUNTRY_CURRENCY_MAP.
  // Without Shopify Payments multi-currency, Shopify always reports EGP for every country
  // in localization.country.currency.iso_code, which would overwrite the correct static map.
  // The static COUNTRY_CURRENCY_MAP above is the authoritative source for country→currency.
  function buildFromDOM() {
    const links = document.querySelectorAll('a[data-value][data-currency]');
    LOG(`buildFromDOM — found ${links.length} country links in DOM (not used for currency mapping)`);
  }

  // ── IP DETECTION ──────────────────────────────────────────
  async function detectCountryByIP() {
    LOG('detectCountryByIP — trying ipapi.co …');
    try {
      const r = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(4000) });
      LOG(`ipapi.co response status: ${r.status}`);
      if (r.ok) {
        const d = await r.json();
        LOG('ipapi.co response data:', d);
        if (d.country_code) {
          LOG(`ipapi.co detected country: ${d.country_code}`);
          return d.country_code.toUpperCase();
        }
        WARN('ipapi.co returned ok but no country_code field');
      } else {
        WARN(`ipapi.co returned non-ok status: ${r.status}`);
      }
    } catch(e) {
      WARN('ipapi.co failed:', e.message || e);
    }

    LOG('detectCountryByIP — trying ip-api.com fallback …');
    try {
      const r = await fetch('https://ip-api.com/json/?fields=countryCode', { signal: AbortSignal.timeout(4000) });
      LOG(`ip-api.com response status: ${r.status}`);
      if (r.ok) {
        const d = await r.json();
        LOG('ip-api.com response data:', d);
        if (d.countryCode) {
          LOG(`ip-api.com detected country: ${d.countryCode}`);
          return d.countryCode.toUpperCase();
        }
        WARN('ip-api.com returned ok but no countryCode field');
      } else {
        WARN(`ip-api.com returned non-ok status: ${r.status}`);
      }
    } catch(e) {
      WARN('ip-api.com failed:', e.message || e);
    }

    ERR('detectCountryByIP — both APIs failed, returning null');
    return null;
  }

  // ── EXCHANGE RATES ────────────────────────────────────────
  const RATE_LS_KEY = 'tina_rates_v2';
  const RATE_TTL_MS = 60 * 60 * 1000; // 1 hour

  function loadCachedRates() {
    try {
      const raw    = localStorage.getItem(RATE_LS_KEY);
      const cached = JSON.parse(raw || 'null');
      if (!cached) { LOG('loadCachedRates — no cache entry found'); return false; }
      if (!cached.ts) { WARN('loadCachedRates — cache entry missing timestamp'); return false; }
      const ageMin = Math.round((Date.now() - cached.ts) / 60000);
      if ((Date.now() - cached.ts) >= RATE_TTL_MS) {
        WARN(`loadCachedRates — cache expired (${ageMin} min old, TTL is 60 min)`);
        return false;
      }
      if (!cached.rates) { WARN('loadCachedRates — cache has no rates object'); return false; }
      rateCache = cached.rates;
      LOG(`loadCachedRates — hit ✓ (${ageMin} min old, ${Object.keys(rateCache).length} currencies)`);
      return true;
    } catch(e) {
      ERR('loadCachedRates — JSON parse error:', e.message);
      return false;
    }
  }

  function persistRates() {
    try {
      localStorage.setItem(RATE_LS_KEY, JSON.stringify({ ts: Date.now(), rates: rateCache }));
      LOG(`persistRates — saved ${Object.keys(rateCache).length} currencies to localStorage`);
    } catch(e) {
      WARN('persistRates — could not write to localStorage:', e.message);
    }
  }

  function fetchRates() {
    if (ratesPromise) { LOG('fetchRates — reusing in-flight promise'); return ratesPromise; }

    if (loadCachedRates()) {
      LOG('fetchRates — using fresh localStorage cache, skipping API');
      ratesPromise = Promise.resolve();
      return ratesPromise;
    }

    LOG('fetchRates — cache miss, starting API fetch …');
    ratesPromise = (async () => {
      // Primary: @fawazahmed0/currency-api via jsDelivr CDN
      const primaryURL = `https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/${BASE_CURRENCY.toLowerCase()}.json`;
      LOG('fetchRates [1/3] trying jsDelivr CDN:', primaryURL);
      try {
        const t0 = Date.now();
        const r  = await fetch(primaryURL, { signal: AbortSignal.timeout(5000) });
        LOG(`fetchRates [1/3] jsDelivr responded in ${Date.now()-t0}ms, status: ${r.status}`);
        if (r.ok) {
          const d    = await r.json();
          const base = BASE_CURRENCY.toLowerCase();
          if (d && d[base]) {
            rateCache = {};
            Object.entries(d[base]).forEach(([k, v]) => { rateCache[k.toUpperCase()] = v; });
            rateCache[BASE_CURRENCY] = 1;
            persistRates();
            LOG(`fetchRates [1/3] ✓ jsDelivr success — ${Object.keys(rateCache).length} currencies loaded`);
            LOG(`fetchRates — SEK rate: ${rateCache['SEK']}, EUR: ${rateCache['EUR']}, USD: ${rateCache['USD']}`);
            return;
          }
          WARN('fetchRates [1/3] jsDelivr ok but unexpected JSON shape:', JSON.stringify(d).slice(0, 200));
        } else {
          WARN(`fetchRates [1/3] jsDelivr non-ok status: ${r.status}`);
        }
      } catch(e) {
        WARN('fetchRates [1/3] jsDelivr fetch failed:', e.message || e);
      }

      // Fallback 1: open.er-api.com
      const fallback1URL = `https://open.er-api.com/v6/latest/${BASE_CURRENCY}`;
      LOG('fetchRates [2/3] trying open.er-api.com:', fallback1URL);
      try {
        const t0 = Date.now();
        const r  = await fetch(fallback1URL, { signal: AbortSignal.timeout(5000) });
        LOG(`fetchRates [2/3] open.er-api.com responded in ${Date.now()-t0}ms, status: ${r.status}`);
        if (r.ok) {
          const d = await r.json();
          LOG('fetchRates [2/3] open.er-api.com result field:', d.result);
          if (d.result === 'success') {
            rateCache = d.rates;
            rateCache[BASE_CURRENCY] = 1;
            persistRates();
            LOG(`fetchRates [2/3] ✓ open.er-api.com success — ${Object.keys(rateCache).length} currencies loaded`);
            LOG(`fetchRates — SEK rate: ${rateCache['SEK']}, EUR: ${rateCache['EUR']}, USD: ${rateCache['USD']}`);
            return;
          }
          WARN('fetchRates [2/3] open.er-api.com result !== success:', d.result, d['error-type']);
        } else {
          WARN(`fetchRates [2/3] open.er-api.com non-ok status: ${r.status}`);
        }
      } catch(e) {
        WARN('fetchRates [2/3] open.er-api.com fetch failed:', e.message || e);
      }

      // Fallback 2: hardcoded approximate rates
      ERR('fetchRates [3/3] both live APIs failed — using hardcoded FALLBACK_RATES (approximate)');
      rateCache = { ...FALLBACK_RATES, [BASE_CURRENCY]: 1 };
      LOG(`fetchRates [3/3] FALLBACK_RATES applied — SEK: ${rateCache['SEK']}, EUR: ${rateCache['EUR']}, USD: ${rateCache['USD']}`);
    })();

    return ratesPromise;
  }

  // ── PRICE REWRITING ───────────────────────────────────────
  function getPriceElements() { return document.querySelectorAll('.money'); }

  function parseEGP(el) {
    if (originalPrices.has(el)) return originalPrices.get(el);
    const raw     = el.textContent.trim();
    const cleaned = raw.replace(/^LE\s*/i,'').replace(/\s*EGP\s*$/i,'').replace(/,/g,'').trim();
    const value   = parseFloat(cleaned);
    if (isNaN(value)) {
      WARN(`parseEGP — could not parse price text: "${raw}" (cleaned: "${cleaned}")`);
      return null;
    }
    el.dataset.tinaOriginal = raw;
    originalPrices.set(el, value);
    return value;
  }

  function formatPrice(egp, currency, rate) {
    const fmt      = CURRENCY_FORMAT[currency] || { symbol: currency + ' ', dec: 2 };
    const converted = egp * rate;
    const fixed    = converted.toFixed(fmt.dec);
    const parts    = fixed.split('.');
    parts[0]       = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.symbol + parts.join('.');
  }

  function rewritePrices() {
    const els = getPriceElements();
    LOG(`rewritePrices — found ${els.length} .money elements | activeCurrency: ${activeCurrency} | activeRate: ${activeRate}`);
    if (els.length === 0) {
      WARN('rewritePrices — no .money elements found on page, nothing to convert');
      return;
    }
    if (activeCurrency === BASE_CURRENCY) {
      let restored = 0;
      els.forEach(el => {
        if (el.dataset.tinaOriginal) { el.textContent = el.dataset.tinaOriginal; restored++; }
      });
      LOG(`rewritePrices — restored ${restored} elements to EGP`);
      return;
    }
    let converted = 0, skipped = 0;
    els.forEach(el => {
      const v = parseEGP(el);
      if (v !== null) {
        el.textContent = formatPrice(v, activeCurrency, activeRate);
        converted++;
      } else {
        skipped++;
      }
    });
    LOG(`rewritePrices — converted ${converted} prices to ${activeCurrency}, skipped ${skipped} unparseable`);
    if (converted > 0) LOG(`rewritePrices — sample: ${els[0].textContent}`);
  }

  // ── APPLY CURRENCY ────────────────────────────────────────
  async function applyCurrency(countryCode) {
    const currency = COUNTRY_CURRENCY_MAP[countryCode] || BASE_CURRENCY;
    LOG(`applyCurrency("${countryCode}") → currency: ${currency} | current: ${activeCurrency} | rate: ${activeRate}`);

    if (currency === activeCurrency && activeRate > 0 && activeRate !== 1) {
      LOG('applyCurrency — already applied with valid rate, skipping');
      return;
    }

    activeCurrency = currency;

    if (currency === BASE_CURRENCY) {
      LOG('applyCurrency — EGP selected, restoring original prices');
      activeRate = 1;
      rewritePrices();
      removeBadge();
      return;
    }

    LOG(`applyCurrency — fetching rates for ${currency} …`);
    await fetchRates();
    const rate = rateCache[currency];
    if (!rate) {
      ERR(`applyCurrency — no rate found for ${currency} in rateCache. Keys present: ${Object.keys(rateCache).slice(0,10).join(', ')}`);
      return;
    }
    activeRate = rate;
    LOG(`applyCurrency — applying ${currency} at rate ${rate} (1 EGP = ${rate} ${currency})`);
    rewritePrices();
    showBadge(currency);
  }

  // ── MARKET SWITCH ─────────────────────────────────────────
  function switchShopifyMarket(countryCode) {
    const existingForm = document.querySelector(
      'localization-form form, form[action="/localization"]'
    );
    const token = existingForm
      ? (existingForm.querySelector('input[name="authenticity_token"]') || {}).value || ''
      : '';

    LOG(`switchShopifyMarket("${countryCode}") | token present: ${!!token} | return_to: ${window.location.pathname + window.location.search}`);
    if (!token) WARN('switchShopifyMarket — no CSRF token found, Shopify may reject the request');

    const form    = document.createElement('form');
    form.method   = 'post';
    form.action   = '/localization';
    form.style.cssText = 'display:none';

    [
      ['form_type',          'localization'],
      ['utf8',               '✓'],
      ['_method',            'put'],
      ['authenticity_token', token],
      ['country_code',       countryCode],
      ['return_to',          window.location.pathname + window.location.search],
    ].forEach(([name, value]) => {
      const i = document.createElement('input');
      i.type = 'hidden'; i.name = name; i.value = value;
      form.appendChild(i);
    });

    document.body.appendChild(form);
    LOG('switchShopifyMarket — submitting form now …');
    form.submit();
  }

  // ── CURRENCY BADGE ────────────────────────────────────────
  function removeBadge() {
    const b = document.getElementById('tina-currency-badge');
    if (b) b.remove();
  }

  function showBadge(currency) {
    removeBadge();
    if (currency === BASE_CURRENCY) return;
    const b = document.createElement('div');
    b.id = 'tina-currency-badge';
    b.innerHTML = `
      <span>Prices shown in <strong>${currency}</strong></span>
      <small style="opacity:.6;display:block;font-size:11px;margin-top:2px;">Checkout will be in EGP</small>
      <button onclick="this.parentElement.remove()"
        style="position:absolute;top:6px;right:8px;background:none;border:none;cursor:pointer;font-size:14px;opacity:.5">✕</button>
    `;
    Object.assign(b.style, {
      position:'fixed', bottom:'20px', right:'20px',
      background:'#1a1a1a', color:'#fff',
      padding:'12px 32px 12px 16px', borderRadius:'8px',
      fontSize:'13px', zIndex:'99999',
      boxShadow:'0 4px 20px rgba(0,0,0,.25)',
      lineHeight:'1.4', fontFamily:'inherit',
    });
    document.body.appendChild(b);
  }

  // ── POPUP ─────────────────────────────────────────────────
  function showCountryPickerPopup(detectedCode) {
    if (hasDecided()) return;

    // Build the list of countries to show — prefer DOM-sourced (matches Shopify Markets),
    // augmented with POPUP_META for flag + display text.
    const domCountries = [];
    const seen = new Set();
    document.querySelectorAll('a[data-value][data-currency]').forEach(a => {
      const code = (a.dataset.value || '').trim().toUpperCase();
      const curr = (a.dataset.currency || '').trim().toUpperCase();
      const name = (a.dataset.name    || '').trim();
      if (code.length !== 2 || seen.has(code)) return;
      seen.add(code);
      const meta = POPUP_META[code] || { flag: '🌐', name: name || code, sub: curr };
      domCountries.push({ code, flag: meta.flag, name: meta.name, sub: meta.sub });
    });

    // If DOM list is empty (data-currency not yet live), fall back to static POPUP_META
    const countryList = domCountries.length > 0
      ? domCountries
      : Object.entries(POPUP_META).map(([code, m]) => ({ code, ...m }));

    const detected = detectedCode
      ? countryList.find(c => c.code === detectedCode) || null
      : null;

    // CSS
    const style = document.createElement('style');
    style.textContent = `
      #tina-overlay{position:fixed;inset:0;z-index:999999;background:rgba(10,10,10,.72);
        backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);
        display:flex;align-items:center;justify-content:center;padding:16px;
        opacity:0;transition:opacity .35s ease;font-family:inherit;}
      #tina-overlay.vis{opacity:1;}
      #tina-modal{background:#fff;border-radius:16px;width:100%;max-width:520px;max-height:90vh;
        overflow:hidden;display:flex;flex-direction:column;
        box-shadow:0 32px 80px rgba(0,0,0,.35);
        transform:translateY(28px);transition:transform .4s cubic-bezier(.22,1,.36,1);}
      #tina-overlay.vis #tina-modal{transform:translateY(0);}
      #tina-header{padding:28px 28px 0;text-align:center;}
      #tina-header .eyebrow{font-size:10.5px;letter-spacing:.2em;text-transform:uppercase;color:#aaa;margin-bottom:12px;}
      #tina-header h2{margin:0 0 8px;font-size:21px;font-weight:700;color:#111;line-height:1.3;}
      #tina-header p{margin:0 0 20px;font-size:13px;color:#777;}
      #tina-detected{margin:0 16px 12px;padding:12px 14px;background:#f5f5f5;border-radius:10px;
        display:flex;align-items:center;gap:12px;cursor:pointer;
        border:1.5px solid #e0e0e0;transition:border-color .15s,background .15s;}
      #tina-detected:hover{background:#efefef;border-color:#bbb;}
      .det-flag{font-size:26px;line-height:1;flex-shrink:0;}
      .det-info{flex:1;}
      .det-info strong{display:block;font-size:14px;font-weight:700;color:#111;}
      .det-info span{font-size:12px;color:#888;}
      .det-badge{font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;
        background:#111;color:#fff;padding:3px 8px;border-radius:20px;flex-shrink:0;}
      #tina-divider{display:flex;align-items:center;gap:10px;
        padding:0 16px 6px;font-size:11px;color:#bbb;text-transform:uppercase;letter-spacing:.1em;}
      #tina-divider::before,#tina-divider::after{content:'';flex:1;height:1px;background:#ebebeb;}
      #tina-search-wrap{padding:0 16px 10px;position:relative;}
      #tina-search{width:100%;box-sizing:border-box;padding:10px 14px 10px 36px;
        border:1.5px solid #e0e0e0;border-radius:8px;font-size:13.5px;
        outline:none;color:#111;background:#fafafa;transition:border-color .2s,background .2s;}
      #tina-search:focus{border-color:#111;background:#fff;}
      #tina-search-wrap::before{content:'';position:absolute;left:28px;top:50%;
        transform:translateY(-50%);width:14px;height:14px;
        background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23aaa' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65'/%3E%3C/svg%3E");
        background-repeat:no-repeat;background-size:contain;pointer-events:none;}
      #tina-list{overflow-y:auto;padding:4px 10px 20px;flex:1;}
      #tina-list::-webkit-scrollbar{width:4px;}
      #tina-list::-webkit-scrollbar-thumb{background:#e0e0e0;border-radius:4px;}
      .tina-btn{width:100%;display:flex;align-items:center;gap:12px;
        padding:10px 12px;margin-bottom:3px;background:none;
        border:1.5px solid transparent;border-radius:10px;
        cursor:pointer;text-align:left;transition:background .14s,border-color .14s;}
      .tina-btn:hover{background:#f6f6f6;border-color:#e8e8e8;}
      .tina-btn:active{background:#efefef;}
      .tina-btn .tf{font-size:22px;line-height:1;flex-shrink:0;}
      .tina-btn .ti{flex:1;min-width:0;}
      .tina-btn .ti strong{display:block;font-size:13.5px;font-weight:600;color:#111;}
      .tina-btn .ti span{font-size:11.5px;color:#999;}
      .tina-btn .ta{font-size:14px;color:#ccc;flex-shrink:0;}
      .tina-btn:hover .ta{color:#888;}
      .tina-none{text-align:center;padding:24px 0;color:#bbb;font-size:13px;}
    `;
    document.head.appendChild(style);

    const detectedHTML = detected ? `
      <div id="tina-detected" data-code="${detected.code}" role="button" tabindex="0"
           aria-label="Use ${detected.name}">
        <span class="det-flag">${detected.flag}</span>
        <span class="det-info">
          <strong>${detected.name}</strong>
          <span>${detected.sub}</span>
        </span>
        <span class="det-badge">Detected</span>
      </div>
      <div id="tina-divider">or choose another</div>
    ` : '';

    const others  = detected ? countryList.filter(c => c.code !== detected.code) : countryList;
    const buttons = others.map(c => `
      <button class="tina-btn" data-code="${c.code}" type="button">
        <span class="tf">${c.flag}</span>
        <span class="ti"><strong>${c.name}</strong><span>${c.sub}</span></span>
        <span class="ta">›</span>
      </button>
    `).join('');

    const overlay = document.createElement('div');
    overlay.id    = 'tina-overlay';
    overlay.innerHTML = `
      <div id="tina-modal" role="dialog" aria-modal="true" aria-labelledby="tina-title">
        <div id="tina-header">
          <div class="eyebrow">Tina Jewellery</div>
          <h2 id="tina-title">Where are you shopping from?</h2>
          <p>Select your country to see prices in your local currency.</p>
        </div>
        ${detectedHTML}
        <div id="tina-search-wrap">
          <input id="tina-search" type="text" placeholder="Search country…"
            autocomplete="off" spellcheck="false" />
        </div>
        <div id="tina-list">${buttons}</div>
      </div>
    `;
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => requestAnimationFrame(() => overlay.classList.add('vis')));

    function pickCountry(code) {
      saveCountry(code);
      // Show loading state inside modal
      const modal = overlay.querySelector('#tina-modal');
      if (modal) modal.innerHTML = `
        <div style="padding:60px 40px;text-align:center;">
          <div style="font-size:32px;margin-bottom:16px;">🌍</div>
          <p style="margin:0;font-size:15px;font-weight:600;color:#111;">Setting your region…</p>
          <p style="margin:8px 0 0;font-size:13px;color:#999;">Just a moment</p>
        </div>`;
      // Small delay so the loading UI renders before navigation
      setTimeout(() => switchShopifyMarket(code), 150);
    }

    // Detected banner
    const banner = overlay.querySelector('#tina-detected');
    if (banner) {
      banner.addEventListener('click',   () => pickCountry(banner.dataset.code));
      banner.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') pickCountry(banner.dataset.code);
      });
    }

    // Country list
    overlay.querySelectorAll('.tina-btn').forEach(btn =>
      btn.addEventListener('click', () => pickCountry(btn.dataset.code))
    );

    // Search filter
    overlay.querySelector('#tina-search').addEventListener('input', function () {
      const q   = this.value.trim().toLowerCase();
      const list = overlay.querySelector('#tina-list');
      let any   = false;
      overlay.querySelectorAll('.tina-btn').forEach(btn => {
        const show = !q || btn.textContent.toLowerCase().includes(q);
        btn.style.display = show ? '' : 'none';
        if (show) any = true;
      });
      let noRes = list.querySelector('.tina-none');
      if (!any && !noRes) {
        noRes = document.createElement('p');
        noRes.className   = 'tina-none';
        noRes.textContent = 'No countries found.';
        list.appendChild(noRes);
      } else if (any && noRes) noRes.remove();
    });

    setTimeout(() => overlay.querySelector('#tina-search').focus(), 420);
  }

  // ── FOOTER / HEADER SELECTOR LISTENER ────────────────────
  function attachCountrySelectorListeners() {
    const links = document.querySelectorAll('localization-form a[data-value]');
    const selects = document.querySelectorAll('select[name="country_code"], select[name="country"]');
    LOG(`attachCountrySelectorListeners — ${links.length} country links, ${selects.length} select elements`);

    links.forEach(a => {
      a.addEventListener('click', () => {
        const code = (a.dataset.value || '').toUpperCase();
        LOG(`country link clicked — code: ${code} → currency: ${COUNTRY_CURRENCY_MAP[code] || 'EGP (fallback)'}`);
        // Do NOT read data-currency from DOM — Shopify reports EGP for all countries
        // without native multi-currency. Static COUNTRY_CURRENCY_MAP is authoritative.
        saveCountry(code);
        applyCurrency(code);
      });
    });

    selects.forEach(sel => {
      sel.addEventListener('change', () => {
        const code = sel.value.toUpperCase();
        LOG(`country select changed — code: ${code}`);
        saveCountry(code);
        applyCurrency(code);
      });
    });
  }

  // ── MUTATION OBSERVER ─────────────────────────────────────
  let mutationTimer = null;
  const observer = new MutationObserver(() => {
    if (activeCurrency === BASE_CURRENCY) return;
    clearTimeout(mutationTimer);
    mutationTimer = setTimeout(rewritePrices, 80);
  });

  // ── BOOT ─────────────────────────────────────────────────
  async function boot() {
    LOG('═══ boot() started ═══');
    LOG(`page: ${location.pathname} | readyState: ${document.readyState}`);
    LOG(`Shopify.country: ${window.Shopify?.country} | Shopify.currency: ${JSON.stringify(window.Shopify?.currency)}`);

    buildFromDOM();

    const shopifyCountry = (window.Shopify && window.Shopify.country)
      ? window.Shopify.country.toUpperCase()
      : null;

    const storedCode = getStoredCountry();
    const decidedFlag = hasDecided();
    LOG(`shopifyCountry: ${shopifyCountry} | storedCode: ${storedCode} | hasDecided: ${decidedFlag}`);

    const activeCode = shopifyCountry || storedCode;
    LOG(`activeCode resolved to: ${activeCode || '(none)'}`);

    if (activeCode && activeCode !== 'EG') {
      LOG(`branch: INTERNATIONAL — converting prices for country "${activeCode}"`);

      const hasFreshCache = loadCachedRates();
      if (!hasFreshCache) {
        LOG('no fresh cache — applying FALLBACK_RATES for instant display');
        rateCache = { ...FALLBACK_RATES, [BASE_CURRENCY]: 1 };
      }

      const currency = COUNTRY_CURRENCY_MAP[activeCode] || BASE_CURRENCY;
      LOG(`country "${activeCode}" → currency "${currency}"`);
      activeCurrency = currency;

      if (currency !== BASE_CURRENCY) {
        activeRate = rateCache[currency] || FALLBACK_RATES[currency] || 0;
        LOG(`initial rate for ${currency}: ${activeRate} (source: ${hasFreshCache ? 'cache' : 'fallback'})`);
        if (activeRate > 0) {
          rewritePrices();
          showBadge(currency);
        } else {
          ERR(`rate for ${currency} is 0 or missing — prices NOT converted. rateCache keys: ${Object.keys(rateCache).slice(0,15).join(', ')}`);
        }
      } else {
        LOG(`currency is EGP (base) — no conversion needed`);
      }

      if (activeCode !== storedCode) { LOG(`saving new activeCode "${activeCode}" to localStorage`); saveCountry(activeCode); }
      if (!decidedFlag) { LOG('first time decided — marking as decided'); saveCountry(activeCode); }

      if (!hasFreshCache) {
        LOG('fetching live rates in background …');
        fetchRates().then(() => {
          if (activeCurrency !== BASE_CURRENCY) {
            const liveRate = rateCache[activeCurrency];
            LOG(`background rate update — live ${activeCurrency} rate: ${liveRate} | was using: ${activeRate}`);
            if (liveRate && liveRate !== activeRate) {
              LOG(`rate changed (${activeRate} → ${liveRate}), rewriting prices with accurate rate`);
              activeRate = liveRate;
              rewritePrices();
            } else {
              LOG('live rate matches fallback rate — no price update needed');
            }
          }
        }).catch(e => ERR('background fetchRates failed:', e));
      } else {
        LOG('fresh cache used — no background API call needed');
      }

    } else if (!decidedFlag) {
      LOG('branch: FIRST VISIT — detecting country by IP');
      const detectedCode = await detectCountryByIP();
      LOG(`IP detection result: ${detectedCode || 'null (failed)'}`);

      if (!detectedCode || detectedCode === 'EG') {
        LOG(`Egyptian or unknown visitor (${detectedCode || 'null'}) — no popup, saving silently`);
        saveCountry(detectedCode || 'EG');
      } else {
        LOG(`international visitor detected (${detectedCode}) — showing country picker popup`);
        if (!loadCachedRates()) {
          LOG('pre-loading FALLBACK_RATES before popup');
          rateCache = { ...FALLBACK_RATES, [BASE_CURRENCY]: 1 };
        }
        showCountryPickerPopup(detectedCode);
        LOG('fetching live rates in background (for popup country pick) …');
        fetchRates();
      }
    } else {
      LOG(`branch: EGYPTIAN / DECIDED — activeCode: ${activeCode || 'none'}, hasDecided: ${decidedFlag} — nothing to do`);
    }

    if (activeCurrency !== BASE_CURRENCY) {
      LOG(`scheduling 800ms failsafe rewrite for ${activeCurrency}`);
      setTimeout(() => { LOG('800ms failsafe rewritePrices running'); rewritePrices(); }, 800);
    }

    attachCountrySelectorListeners();
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('cart:updated', rewritePrices);
    document.addEventListener('cart:refresh',  rewritePrices);
    LOG('boot() complete — MutationObserver active, cart events wired');

    setInterval(() => {
      LOG('hourly rate refresh — clearing cache and re-fetching');
      ratesPromise = null;
      localStorage.removeItem(RATE_LS_KEY);
      fetchRates().then(() => {
        if (activeCurrency !== BASE_CURRENCY) {
          activeRate = rateCache[activeCurrency] || activeRate;
          rewritePrices();
        }
      });
    }, RATE_TTL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
